import { Trans } from 'react-i18next'
import { useBoolean } from 'react-use'
import { closestCenter, DndContext } from '@dnd-kit/core'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'

import { Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button, DD } from '@makyoapp/makyo-ui'

import { Language } from '@/lib/language-flag'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useSortableHandle } from '@/hooks/useSortableHandle'
import { AccordionItem } from '@/components/Accordion/Accordion'
import { LanguageDropdown } from '@/components/LanguageModal'

import SortableLanguage from './SortableLanguage'

function LanguageAccordionItem() {
  const { defaultLanguage, languages, updateLanguages } = useDbClientContext()

  const [showModal, setShowModal] = useBoolean(false)
  const [isLangLoading, setIsLangLoading] = useBoolean(false)

  const handleDeleteLanguage = (lng: Language) => {
    updateLanguages(languages.filter((l) => l.ids.locale !== lng.ids.locale))
  }

  const handleMakeDefault = (lng: Language) => {
    updateLanguages(languages, lng.ids.locale)
  }

  const handleAddLanguage = async (lng: Language) => {
    setIsLangLoading(true)
    await updateLanguages([...languages, lng])
    setIsLangLoading(false)
    setShowModal(false)
  }

  const { sensors, handleDragEnd } = useSortableHandle((active, over) => {
    const oldIndex = languages.findIndex((l) => l.ids.locale === active.id)
    const newIndex = languages.findIndex((l) => l.ids.locale === over.id)
    const reorderedLanguages = arrayMove(languages, oldIndex, newIndex)

    updateLanguages(reorderedLanguages)
  })

  return (
    <AccordionItem itemId='setting-language' title={<Trans>Language</Trans>}>
      <div className='flex flex-col gap-1 pb-4'>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={languages.map((l) => l.ids.locale)}>
            {languages.map((lng, index) => {
              return (
                <SortableLanguage
                  key={lng.ids.locale}
                  language={lng}
                  index={index}
                  isDefault={lng.ids.locale === defaultLanguage}
                  onMakeDefault={() => handleMakeDefault(lng)}
                  onDelete={() => handleDeleteLanguage(lng)}
                />
              )
            })}
          </SortableContext>
        </DndContext>
      </div>

      {/* If showModal is true, render the LanguageModal component */}
      {/* {showModal && (
        <LanguageModal
          onHide={() => setShowModal(false)}
          show={showModal}
          isLoading={isLangLoading}
          excludeLanguages={languages}
          onSelect={handleAddLanguage}
        />
      )}
      <Button icon={<Plus16 />} disabled={isLangLoading} onClick={() => setShowModal(true)} className='w-full'>
        <Trans>New Language</Trans>
      </Button> */}

      <DD
        menuAlwaysRender={false}
        id={`languange-select-dd`}
        toggleNoButton
        menuClassName='w-[260px] z-303'
        menuWrapperClassName='sdd-menu-subc text-m-m text-zinc-700 dark:text-slate-300'
        className='w-full block'
        menuDefaultSize={false}
        placement={'auto-end'}
        show={showModal}
        portalMenu
        onToggle={(nextShow, meta) => {
          let event = meta.originalEvent
          const tgt = event?.target as HTMLElement
          setShowModal(nextShow)
        }}
        toggle={(active, toggleProps) => {
          return (
            <Button icon={<Plus16 />} disabled={isLangLoading} className='w-full' {...toggleProps}>
              <Trans>New Language</Trans>
            </Button>
          )
        }}
      >
        <LanguageDropdown
          onHide={() => setShowModal(false)}
          show={showModal}
          isLoading={isLangLoading}
          excludeLanguages={languages}
          onSelect={handleAddLanguage}
        />
      </DD>
    </AccordionItem>
  )
}

export default LanguageAccordionItem
